import type { MouseEventHandler, ReactNode, FC } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@packages/shared/src/ui/Button/Button';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { DisplaySettingsMobileFragmentFragmentDoc } from '@packages/gql/generated/shopping/DisplaySettingsMobileFragmentFragmentDoc';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';

import { cn } from '@packages/shared/src/utils/cn/cn';
import { useDisplayMode } from '../../hooks/useDisplayMode';
import { usePaging } from '../../hooks/usePaging';
import { PRODUCT_LIST_ID } from '../../ids';

/* GraphQL */ `
  fragment DisplaySettingsMobileFragment on SearchProductResult {
    ...UsePagingFragment
    ...UseDisplayModeFragment
  }
`;

const messages = defineMessages({
  gallery: {
    id: 'search.productList.displaySettings.displayModes.gallery',
    defaultMessage: 'Galerieansicht',
  },
  list: {
    id: 'search.productList.displaySettings.displayModes.list',
    defaultMessage: 'Listenansicht',
  },
  productListDisplayOption: {
    id: 'search.productList.displaySettings.mobile.view',
    defaultMessage: 'Produktlistenansicht',
  },
});

export type DisplaySettingsMobileProps = {
  maskedData: FragmentType<typeof DisplaySettingsMobileFragmentFragmentDoc>;
};
type ProductListViewButtonProps = {
  children: ReactNode;
  /** Function to push a new display mode */
  pushDisplayModeChange: MouseEventHandler<HTMLButtonElement>;
  isActive: boolean;
};

/**
 * Button to switch the product list view mode.
 * When clicked, it updates the display mode.
 */
const ProductListViewButton = ({
  children,
  isActive,
  pushDisplayModeChange,
}: ProductListViewButtonProps) => (
  <Button
    size="base"
    className="bg-color-background-base enabled:focus-visible:bg-color-background-base enabled:hover:bg-color-background-base mr-0.5 flex flex-1 gap-1 border-none p-1"
    onClick={pushDisplayModeChange}
    aria-pressed={isActive}
  >
    {children}
  </Button>
);
/**
 * Component to allow the user to change the display settings for the product list
 * */
export const DisplaySettingsMobile: FC<DisplaySettingsMobileProps> = ({ maskedData }) => {
  const data = unmask<typeof DisplaySettingsMobileFragmentFragmentDoc>(maskedData);
  const { formatMessage } = useIntl();

  const { pagingMode, pushPageChange, pushPagingMode } = usePaging(data);
  const { displayMode, pushDisplayModeChange } = useDisplayMode(data);
  const {
    search: { paginationMode },
  } = useConfig();

  if (paginationMode === 'paginationAndEndlessScrolling' && pagingMode === 'infinite') {
    pushPageChange(1, true);
    pushPagingMode('pagination');
  }
  return (
    <nav
      aria-label={formatMessage(messages.productListDisplayOption)}
      aria-controls={PRODUCT_LIST_ID}
      className="flex w-full flex-col items-center justify-center sm:flex-row"
    >
      <ProductListViewButton
        pushDisplayModeChange={() => pushDisplayModeChange('cards')}
        isActive={displayMode === 'cards'}
      >
        <div
          className={cn(
            'icon-emp-gallery h-3 w-3',
            displayMode === 'cards' && 'text-color-primary',
          )}
        />
        <FormattedMessage
          key="search.productList.displaySettings.displayModes.gallery-text"
          {...messages.gallery}
        />
      </ProductListViewButton>
      <ProductListViewButton
        pushDisplayModeChange={() => pushDisplayModeChange('moreInformation')}
        isActive={displayMode === 'moreInformation'}
      >
        <div
          className={cn(
            'icon-emp-list h-3 w-3',
            displayMode === 'moreInformation' && 'text-color-primary',
          )}
        />
        <FormattedMessage
          key="search.productList.displaySettings.displayModes.list-text"
          {...messages.list}
        />
      </ProductListViewButton>
    </nav>
  );
};
